import { useMutation } from '@tanstack/react-query';
import { useBunja } from 'bunja/react';
import { isEmpty } from 'es-toolkit/compat';
import { useAtomValue, useSetAtom } from 'jotai';
import { usePathname, useRouter } from 'next/navigation';
import { ChangeEventHandler, ReactNode, useState } from 'react';
import { useEffectOnce } from 'react-use';

import { putTradeModeApi } from '@flipster/apis/v2/trade';
import { TradeMode } from '@flipster/apis/v2/trade/types';
import { FormattedMessage } from '@flipster/i18n';
import { accountBunja } from '@flipster/r230522/original/account';
import { ordersBunja } from '@flipster/r230522/original/order';
import { Modal } from '@flipster/react/design-system/modal';
import { RadioButton } from '@flipster/react/design-system/radio-button';
import { snackbar } from '@flipster/react/design-system/snackbar';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import { alert } from '@flipster/ui/Alert';
import { getErrorCode, getErrorMessage } from '@flipster/utils/error';
import segment from '@flipster/utils/segment';
import { TradeModeEvent } from '@flipster/utils/segment/events/trade-mode';

import { maxPositionLimit } from '@web/constants/position';

import { perpEtcTabTypeBunja } from '../../../trade/_misc/etc-section/perpetual';
import { yourPositionsBunja } from '../../../trade/_misc/etc-section/perpetual/your-postions/state';
import { spotEtcTabTypeBunja } from '../../../trade/_misc/etc-section/spot';
import { tradeSymbolBunja } from '../../../trade/_misc/symbol';

interface PositionModeInfo {
  label: ReactNode;
  desc: ReactNode;
  value: TradeMode;
  additionalContent?: ReactNode;
}

interface Props {
  onBack: () => void;
  onError: (error: unknown) => void;
  onSuccess: () => void;
}

const PositionModes = ({ onBack: onBackProp, onError, onSuccess }: Props) => {
  const { v2AccountAtom } = useBunja(accountBunja);
  const account = useAtomValue(v2AccountAtom);
  const { pendingPositionsAtom } = useBunja(ordersBunja);
  const { positionTabTypeAtom } = useBunja(yourPositionsBunja);
  const orders = useAtomValue(pendingPositionsAtom);

  const { symbolInfoAtom } = useBunja(tradeSymbolBunja);
  const symbolInfo = useAtomValue(symbolInfoAtom);
  const { setTabType: setPerpTabType } = useBunja(perpEtcTabTypeBunja);
  const { tabTypeAtom: spotTabTypeAtom } = useBunja(spotEtcTabTypeBunja);
  const setSpotTabType = useSetAtom(spotTabTypeAtom);
  const setPositionTabType = useSetAtom(positionTabTypeAtom);

  const [positionMode, setPositionMode] = useState<TradeMode>(account?.tradeMode || 'OneWay');
  const router = useRouter();
  const pathname = usePathname();

  const isValid = account && account.tradeMode !== positionMode;

  const handleTradeModeChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setPositionMode(e.target.value as TradeMode);
  };

  const { isPending, mutateAsync } = useMutation({
    mutationKey: ['putTradeModeApi'],
    mutationFn: putTradeModeApi,
    onError(error) {
      const errorCode = getErrorCode(error);
      if (['RemainingOrdersExist', 'RemainingPositionsExist'].includes(errorCode)) {
        alert.show({
          title: <FormattedMessage id='Assets.r251027.Close_all_trades' />,
          message: getErrorMessage(error),
          confirmText: <FormattedMessage id='Common.r251027.View_orders' />,
          closeText: <FormattedMessage id='Common.r250401.Cancel' />,
          withClose: true,
          onConfirm: () => {
            segment.track(TradeModeEvent.CloseAllTradesViewOrdersClick);
            if (!pathname.includes('/trade')) router.push('/trade/perpetual?tab=positions');
            else {
              if (symbolInfo?.isSpot) setSpotTabType('positions');
              else setPerpTabType('positions');
            }

            // pending order가 있으면 pending tab으로 이동, 없으면 open tab으로 이동
            if (!isEmpty(orders)) setPositionTabType('pending');
            else setPositionTabType('open');
          },
        });
        return;
      }
      snackbar.error({ message: getErrorMessage(error) });
    },
  });

  const switchPositionMode = async () => {
    segment.track(TradeModeEvent.TradeSettingsConfirmClick, {
      setting_name: 'Position modes',
      option_name: positionMode === 'OneWay' ? 'oneway' : 'multiposition',
    });

    try {
      await mutateAsync({ mode: positionMode });
      const text = {
        OneWay: 'one-way',
        MultiplePositions: 'multi-position',
      };
      snackbar.success({
        message: (
          <FormattedMessage
            id='Autogen_common.r251126.s_You_are_now_using_arg0_mode'
            values={{ arg0: text[positionMode] }}
          />
        ),
      });
      onSuccess();
    } catch (error) {
      onError(error);
    }
  };

  const onBack = () => {
    segment.track(TradeModeEvent.TradeSettingsBackClick, { setting_name: 'Position modes' });
    onBackProp();
  };

  useEffectOnce(() => {
    segment.track(TradeModeEvent.SetPositionModePageView);
  });

  return (
    <>
      <Modal.Header>
        <Modal.Title label={<FormattedMessage id='Account.r250401.Menu_position_modes' />} />
      </Modal.Header>
      <Modal.Content>
        <div className={stack({ gap: 10 })}>
          {positionModeItems?.map((item) => (
            <fieldset
              key={item.value}
              data-selected={positionMode === item.value ? '' : undefined}
              className={css({
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                rounded: 12,
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'border.default.level-1',
                _selected: { borderColor: 'border.default.level-5' },
              })}
            >
              <div
                className={css({ display: 'flex', flexDirection: 'column', gap: 6, w: '100%', px: 20, pt: 16, pb: 20 })}
              >
                <RadioButton
                  name='tradeMode'
                  value={item.value}
                  checked={positionMode === item.value}
                  onChange={handleTradeModeChange}
                  css={css.raw({ color: 'content.default.level-5' })}
                >
                  {item.label}
                </RadioButton>
                <div
                  className={css({
                    textStyle: 'Body/S/Regular',
                    color: 'content.default.level-2',
                    cursor: 'pointer',
                  })}
                  onClick={() => setPositionMode(item.value)}
                >
                  {item.desc}
                </div>
              </div>
              {item.additionalContent}
            </fieldset>
          ))}

          <div className={hstack({ gap: 8, alignItems: 'center', color: 'content.default.level-4' })}>
            <InformationCircleIcon size={16} className={css({ flexShrink: 0 })} />
            <p className={css({ textStyle: 'Body/S/Regular', whiteSpace: 'break-spaces' })}>
              <FormattedMessage id='Autogen_common.r251126.s_This_setting_applies_to_all_perpetual_swap_trades_on_Flipster' />
            </p>
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.Button
          hierarchy='secondary'
          size='large'
          color='neutral'
          label={<FormattedMessage id='Common.r250401.Back' />}
          onClick={onBack}
        />
        <Modal.Button
          hierarchy='primary'
          size='large'
          color='accent'
          label={<FormattedMessage id='Common.r250401.Confirm' />}
          onClick={switchPositionMode}
          disabled={!isValid || isPending}
        />
      </Modal.Footer>
    </>
  );
};

export default PositionModes;

const additionalContentStyle = css({
  w: '100%',
  px: 20,
  py: 12,
  bg: 'background.default.level-1',
  textStyle: 'Text/S/Regular',
  color: 'content.default.level-2',
});

const positionModeItems: PositionModeInfo[] = [
  {
    label: <FormattedMessage id='Assets.r251027.Multi_position_mode' />,
    desc: (
      <FormattedMessage
        id='Assets.r251027.You_can_hold_up_count_multiple_long_short_symbol'
        values={{ count: maxPositionLimit }}
      />
    ),
    value: 'MultiplePositions',
  },
  {
    label: <FormattedMessage id='Assets.r251027.One_way_mode' />,
    desc: <FormattedMessage id='Assets.r251027.You_can_hold_either_long_short_symbol' />,
    value: 'OneWay',
    additionalContent: (
      <div className={additionalContentStyle}>
        <FormattedMessage id='Position_mode.r260303.Oneway' />
      </div>
    ),
  },
];
